import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { Typeahead } from 'react-bootstrap-typeahead'; // Import Typeahead
import 'react-bootstrap-typeahead/css/Typeahead.css'; // Import Typeahead CSS
import { getMusicaById, updateMusica, getFornitori, deleteMusicaImage, generateMusicaWebDescription, getArtisti, getGeneriMusica, getCaseDiProduzioneMusica, getCategorieMusica, getSupportiMusica, getUbicazioniMusica, searchDiscogsReleases, updateMusicaFromDiscogs, updateUbicazioniMusica, getCompetitivePrice } from '../services/api';
import { calcolaPrezzoIbs, useImpostazioniPrezzi } from '../utils/prezziIbs';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';

const MusicaEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/musica');
  const [musica, setMusica] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [artisti, setArtisti] = useState([]);
  const [generi, setGeneri] = useState([]);
  const [caseDiProduzione, setCaseDiProduzione] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [supporti, setSupporti] = useState([]);
  const [ubicazioni, setUbicazioni] = useState([]);
  const [ubicazioniRows, setUbicazioniRows] = useState([]); // [{ ubicazione, quantita }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // State for Discogs Import
  const [discogsSearchTerm, setDiscogsSearchTerm] = useState('');
  const [discogsResults, setDiscogsResults] = useState([]);
  const [showDiscogsResults, setShowDiscogsResults] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const debouncedSearch = React.useRef(
    debounce(async (query) => {
        if (query.length > 2) {
            try {
                const response = await searchDiscogsReleases(query);
                setDiscogsResults(response.data);
                setShowDiscogsResults(true);
            } catch (error) {
                console.error("Errore nella ricerca Discogs:", error);
                setError("Errore durante la ricerca su Discogs.");
            }
        } else {
            setShowDiscogsResults(false);
        }
    }, 500)
  ).current;

  useEffect(() => {
    debouncedSearch(discogsSearchTerm);
  }, [discogsSearchTerm, debouncedSearch]);

  const { percentualeIbs, markupFissoIbs } = useImpostazioniPrezzi();
  const location = useLocation();
  const [isCalcoloPrezzo, setIsCalcoloPrezzo] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [musicaRes, fornitoriRes, artistiRes, generiRes, caseProdRes, catRes, suppRes, ubicazioniRes] = await Promise.all([
        getMusicaById(id),
        getFornitori(),
        getArtisti(),
        getGeneriMusica(),
        getCaseDiProduzioneMusica(),
        getCategorieMusica(),
        getSupportiMusica(),
        getUbicazioniMusica()
      ]);
      setMusica(musicaRes.data);
      setUbicazioniRows((musicaRes.data.ubicazioni_stock || []).map(r => ({ ubicazione: r.ubicazione, quantita: r.quantita })));
      setFornitori(fornitoriRes.data.fornitori);
      setArtisti(artistiRes.data);
      setGeneri(generiRes.data);
      setCaseDiProduzione(caseProdRes.data);
      setCategorie(catRes.data);
      setSupporti(suppRes.data);
      setUbicazioni(ubicazioniRes.data);
      if (musicaRes.data.immagine_url) {
        const imageUrl = musicaRes.data.immagine_url;
        const finalUrl = imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${imageUrl}`;
        setImagePreviewUrl(finalUrl);
      } else {
        setImagePreviewUrl(null);
      }
    } catch (err) {
      console.error("Errore nel caricamento dei dati:", err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Doppio clic dalla lista su una colonna: porta direttamente alla sezione corrispondente
  useEffect(() => {
    if (loading || !musica || !location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = target.querySelector('input, select, textarea');
      if (input) input.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.hash]);

  useEffect(() => {
    if (selectedImage) {
      setImagePreviewUrl(URL.createObjectURL(selectedImage));
    } else if (musica && !musica.immagine_url) {
      setImagePreviewUrl(null);
    }
  }, [selectedImage, musica]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number') {
      newValue = value === '' ? null : parseFloat(value);
    } else if (name === 'fornitore_id') {
      newValue = value === '' ? null : parseInt(value);
    } else if (name === 'sku') {
      newValue = value.toUpperCase();
    }

    const aggiornamenti = { [name]: newValue };
    if (name === 'prezzo_negozio') {
      // Il prezzo IBS segue sempre il prezzo negozio; il base segue finché non si usa il markup (🚚)
      aggiornamenti.prezzo_ibs = calcolaPrezzoIbs(newValue, percentualeIbs, markupFissoIbs);
      aggiornamenti.prezzo_negozio_base = newValue;
    }
    setMusica({
      ...musica,
      ...aggiornamenti,
    });
  };


  const handleCalcolaPrezzoCompetitivo = async () => {
    const ean = musica?.barcode_ibs;
    if (!ean) {
      toast.error('Inserisci prima il Barcode IBS (EAN)');
      return;
    }
    setIsCalcoloPrezzo(true);
    try {
      const res = await getCompetitivePrice(ean, musica?.condizione || 'Nuovo', musica?.costo_acquisto);
      const { competitive_price, found } = res.data;
      if (found && competitive_price !== null) {
        setMusica(prev => ({ ...prev, prezzo_ibs: competitive_price }));
        toast.success(`Prezzo IBS €${competitive_price.toFixed(2)}: batte ${res.data.concorrente} (${res.data.mercato_totale?.toFixed(2)}€ consegnato)${res.data.battibile === false ? ' — impostato il minimo per il margine' : ''}`);
      } else {
        toast('Nessun prezzo competitor trovato per questo EAN/condizione', { icon: '⚠️' });
      }
    } catch (err) {
      toast.error('Errore nel calcolo del prezzo: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setIsCalcoloPrezzo(false);
    }
  };

  const quantitaTotale = ubicazioniRows.reduce((sum, r) => sum + (parseInt(r.quantita) || 0), 0);

  const handleUbicazioneRowChange = (index, field, value) => {
    setUbicazioniRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: field === 'quantita' ? (parseInt(value) || 0) : value } : r));
  };

  const handleAddUbicazioneRow = () => {
    setUbicazioniRows(prev => [...prev, { ubicazione: '', quantita: 0 }]);
  };

  const handleRemoveUbicazioneRow = (index) => {
    setUbicazioniRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleTypeaheadChange = (name, value) => {
    let selectedValue = Array.isArray(value) ? value[0] : value;
    if (typeof selectedValue === 'object' && selectedValue !== null && selectedValue.customOption) {
      selectedValue = selectedValue.label;
    }
    setMusica(prevMusica => ({
      ...prevMusica,
      [name]: selectedValue || '',
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedImage(file);
  };

  const handleDeleteImage = async () => {
    if (window.confirm('Sei sicuro di voler eliminare l\'immagine?')) {
      try {
        await deleteMusicaImage(id);
        setMusica(prevMusica => ({ ...prevMusica, immagine_url: null }));
        setSelectedImage(null);
        setImagePreviewUrl(null);
        toast.success('Immagine eliminata con successo!');
      } catch (err) {
        console.error("Errore nell'eliminazione dell'immagine:", err);
        toast.error("Errore nell'eliminazione dell'immagine.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const musicaDataForJson = { ...musica };
      delete musicaDataForJson.fornitore; // Rimuovi oggetti annidati prima dello stringify
      // Quantità e ubicazione sono derivate dalle righe ubicazioni_stock, salvate a parte
      delete musicaDataForJson.quantita;
      delete musicaDataForJson.ubicazione;

      const formData = new FormData();
      formData.append('musica_data', JSON.stringify(musicaDataForJson));

      if (selectedImage) {
        formData.append('immagine_file', selectedImage);
      }

      await updateMusica(id, formData);

      const righeValide = ubicazioniRows
        .map(r => ({ ubicazione: (r.ubicazione || '').trim(), quantita: parseInt(r.quantita) || 0 }))
        .filter(r => r.ubicazione && r.quantita > 0);
      await updateUbicazioniMusica(id, righeValide);

      toast.success('Musica aggiornata con successo!');
      navigate('/musica');
    } catch (error) {
      console.error("Errore nell'aggiornamento della musica:", error);
      const errorMessage = error.response?.data?.detail ? JSON.stringify(error.response.data.detail) : "Errore nell'aggiornamento della musica.";
      toast.error(errorMessage);
    }
  };

  const handleGenerateWebDescription = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await generateMusicaWebDescription(id);
      setMusica(response.data);
      toast.success('Descrizione web generata con successo!');
    } catch (err) {
      setError('Errore durante la generazione della descrizione web.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImportDiscogs = async (releaseId) => {
    setIsImporting(true);
    setError('');
    try {
        const response = await updateMusicaFromDiscogs(id, releaseId);
        const importedMusica = response.data.musica;
        
        setMusica(prev => ({
            ...importedMusica, // Usa i nuovi dati come base
            id: prev.id,       // Ma assicurati di mantenere l'ID originale
            categoria: importedMusica.categoria || "MUSICA",
        }));

        if (importedMusica.immagine_url) {
            setImagePreviewUrl(importedMusica.immagine_url.startsWith('http') ? importedMusica.immagine_url : `${process.env.REACT_APP_API_BASE_URL}${importedMusica.immagine_url}`);
        }

        toast.success(`Dati importati da Discogs con successo! ${response.data.image_message}`);
        setShowDiscogsResults(false);
    } catch (error) {
        setError(error.response?.data?.detail || "Errore durante l'importazione da Discogs.");
        console.error("Errore importazione Discogs:", error);
    } finally {
        setIsImporting(false);
    }
  };

  if (loading) {
    return <Spinner animation="border" />;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!musica) {
    return <Alert variant="warning">Musica non trovata.</Alert>;
  }

  return (
    <Container>
      <div className="sticky-action-bar">
        <Row className="align-items-center">
            <Col>
                <h2>Modifica Musica</h2>
            </Col>
            <Col className="text-end">
                <Button variant="info" onClick={handleGenerateWebDescription} disabled={loading} className="me-2">
                    {loading ? 'Generazione...' : 'Genera Descrizione Web'}
                </Button>
                <Button variant="secondary" onClick={tornaIndietro} className="me-2">
                    Torna alla Lista
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Salva Modifiche'}
                </Button>
            </Col>
        </Row>
      </div>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

      <Form onSubmit={(e) => e.preventDefault()}>

        {/* Card: CODICI */}
        <Card className="mb-3">
          <Card.Header>Codici</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control type="text" name="sku" value={musica.sku || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Barcode</Form.Label>
                  <Form.Control type="text" name="barcode" value={musica.barcode || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Barcode IBS</Form.Label>
                  <Form.Control type="text" name="barcode_ibs" value={musica.barcode_ibs || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Importa Dati */}
        <Card className="mb-3">
            <Card.Header>Importa Dati da Discogs</Card.Header>
            <Card.Body>
                <Form.Group className="mb-3">
                    <Form.Label>Cerca su Discogs</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Cerca per artista, album, barcode..."
                            value={discogsSearchTerm}
                            onChange={(e) => setDiscogsSearchTerm(e.target.value)}
                        />
                    </InputGroup>
                    {showDiscogsResults && (
                        <div className="list-group" style={{ zIndex: 1000, position: 'absolute' }}>
                            {discogsResults.map(release => (
                                <div key={release.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                                    {release.title} ({release.year})
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleImportDiscogs(release.id)}
                                        disabled={isImporting}
                                    >
                                        Importa
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Form.Group>
            </Card.Body>
        </Card>

        {/* Card: Dati Discogs */}
        <Card className="mb-3">
          <Card.Header>Dati Discogs</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Discogs ID</Form.Label>
                  <Form.Control 
                    type="text" 
                    name="discogs_id" 
                    value={musica.discogs_id || ''} 
                    onChange={handleChange}
                    placeholder="ID release Discogs"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Rating Discogs (1-5)</Form.Label>
                  <Form.Control 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    max="5"
                    name="rating_discogs" 
                    value={musica.rating_discogs || ''} 
                    onChange={handleChange}
                    placeholder="Media voti Discogs"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Numero Voti Discogs</Form.Label>
                  <Form.Control 
                    type="number" 
                    name="rating_discogs_count" 
                    value={musica.rating_discogs_count || ''} 
                    onChange={handleChange}
                    placeholder="Conteggio voti"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Informazioni Principali */}
        <Card className="mb-3">
          <Card.Header>Informazioni Principali</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titolo Album</Form.Label>
                  <Form.Control type="text" name="titolo" value={musica.titolo || ''} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Anno</Form.Label>
                  <Form.Control type="number" name="anno" value={musica.anno || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Artista</Form.Label>
                  <Typeahead
                    id="artista-typeahead"
                    options={artisti}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('artista', selected)}
                    onInputChange={(text) => handleTypeaheadChange('artista', text)}
                    selected={musica.artista ? [musica.artista] : []}
                    placeholder="Seleziona o inserisci un artista"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Genere</Form.Label>
                  <Typeahead
                    id="genere-typeahead"
                    options={generi}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('genere', selected)}
                    onInputChange={(text) => handleTypeaheadChange('genere', text)}
                    selected={musica.genere ? [musica.genere] : []}
                    placeholder="Seleziona o inserisci un genere"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Descrizione</Form.Label>
              <Form.Control as="textarea" rows={3} name="descrizione" value={musica.descrizione || ''} onChange={handleChange} />
            </Form.Group>
          </Card.Body>
        </Card>

        {/* Card: Dettagli Prodotto */}
        <Card className="mb-3">
          <Card.Header>Dettagli Prodotto</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Casa di Produzione</Form.Label>
                  <Typeahead
                    id="casa-di-produzione-typeahead"
                    options={caseDiProduzione}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('casa_di_produzione', selected)}
                    onInputChange={(text) => handleTypeaheadChange('casa_di_produzione', text)}
                    selected={musica.casa_di_produzione ? [musica.casa_di_produzione] : []}
                    placeholder="Seleziona o inserisci una casa di produzione"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria</Form.Label>
                  <Typeahead
                    id="categoria-typeahead"
                    options={categorie}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('categoria', selected)}
                    onInputChange={(text) => handleTypeaheadChange('categoria', text)}
                    selected={musica.categoria ? [musica.categoria] : []}
                    placeholder="Seleziona o inserisci una categoria"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Supporto</Form.Label>
                  <Typeahead
                    id="supporto-typeahead"
                    options={supporti}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('supporto', selected)}
                    onInputChange={(text) => handleTypeaheadChange('supporto', text)}
                    selected={musica.supporto ? [musica.supporto] : []}
                    placeholder="Seleziona o inserisci un supporto"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantità <span className="text-muted fw-normal small">(somma delle ubicazioni sotto)</span></Form.Label>
                  <Form.Control type="number" value={quantitaTotale} readOnly disabled />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Prezzi */}
        <Card className="mb-3" id="prezzi">
          <Card.Header>Prezzi</Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo Negozio</Form.Label>
                  <InputGroup>
                    <Form.Control type="number" step="0.01" name="prezzo_negozio" value={musica.prezzo_negozio || ''} onChange={handleChange} />
                  </InputGroup>
                  {musica.prezzo_negozio_base != null && (
                    <Form.Text className="text-muted">Prezzo base: €{Number(musica.prezzo_negozio_base).toFixed(2)}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Prezzo IBS <span className="text-muted small">(dal prezzo base: +{percentualeIbs}% +{markupFissoIbs}€)</span></Form.Label>
                  <InputGroup>
                    <Form.Control type="number" step="0.01" name="prezzo_ibs" value={musica.prezzo_ibs || ''} disabled readOnly />
                    <Button variant="outline-secondary" onClick={handleCalcolaPrezzoCompetitivo} disabled={isCalcoloPrezzo || !musica.barcode_ibs} title="Sovrascrivi con il prezzo competitivo da IBS Marketplace (P11)">
                      {isCalcoloPrezzo ? <Spinner as="span" animation="border" size="sm" /> : '💰 P11'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Costo Acquisto</Form.Label>
                  <Form.Control type="number" step="0.01" name="costo_acquisto" value={musica.costo_acquisto || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>IVA (%)</Form.Label>
                  <Form.Control type="number" step="0.01" name="iva" value={musica.iva || ''} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Image Upload Section */}
        <Card className="mb-3">
          <Card.Header>Immagine Musica</Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <Form.Group controlId="formImmagineMusica" className="mb-3">
                  <Form.Label>Carica/Modifica Immagine</Form.Label>
                  <Form.Control type="file" onChange={handleFileChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                {imagePreviewUrl && (
                  <div className="mt-3">
                    <p>Anteprima Immagine:</p>
                    <img 
                      src={imagePreviewUrl} 
                      alt={musica.titolo}
                      style={{ maxWidth: '200px', height: 'auto' }}
                      className="img-thumbnail"
                    />
                    <Button variant="danger" size="sm" className="ms-2" onClick={handleDeleteImage}>
                      Elimina Immagine
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Fornitore e Catalogo */}
        <Card className="mb-3">
            <Card.Header>Fornitore e Catalogo</Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Fornitore</Form.Label>
                            <Form.Select name="fornitore_id" value={musica.fornitore_id || ''} onChange={handleChange}>
                                <option value="">Seleziona fornitore</option>
                                {fornitori.map(f => <option key={f.id} value={f.id}>{f.denominazione}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3" id="ubicazione">
                            <Form.Label>Ubicazioni <span className="text-muted fw-normal small">(una riga per ogni locazione fisica)</span></Form.Label>
                            {ubicazioniRows.map((riga, index) => (
                                <InputGroup className="mb-2" key={index}>
                                    <Form.Control
                                        type="text"
                                        value={riga.ubicazione}
                                        onChange={(e) => handleUbicazioneRowChange(index, 'ubicazione', e.target.value)}
                                        list="ubicazioni-musica-list"
                                        placeholder="Ubicazione"
                                    />
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        value={riga.quantita}
                                        onChange={(e) => handleUbicazioneRowChange(index, 'quantita', e.target.value)}
                                        style={{ maxWidth: 100 }}
                                        placeholder="Qtà"
                                    />
                                    <Button variant="outline-danger" onClick={() => handleRemoveUbicazioneRow(index)}>&times;</Button>
                                </InputGroup>
                            ))}
                            <datalist id="ubicazioni-musica-list">
                                {ubicazioni.map(u => <option key={u} value={u} />)}
                            </datalist>
                            <Button variant="outline-secondary" size="sm" onClick={handleAddUbicazioneRow}>+ Aggiungi ubicazione</Button>
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Check type="switch" name="fuori_catalogo" label="Fuori Catalogo" checked={musica.fuori_catalogo || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="da_pubblicare_su_ibs" label="Presente su IBS" checked={musica.da_pubblicare_su_ibs || false} onChange={handleChange} className="d-inline-block me-3" />
                    <Form.Check type="switch" name="in_vetrina" label="In Vetrina" checked={musica.in_vetrina || false} onChange={handleChange} className="d-inline-block" />
                </Form.Group>
            </Card.Body>
        </Card>


      </Form>
    </Container>
  );
};

export default MusicaEdit;
